import { cur } from '@/lib/currency';
import { useEffect, useState } from 'react';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Factory, Plus, Trash2, Edit, DollarSign } from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';

export default function BillOfMaterialsPage() {
  const [boms, setBoms] = useState([]);
  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ product_name: '', product_code: '', labor_cost: 0, overhead_cost: 0, yield_quantity: 1, notes: '', materials: [{ name: '', quantity: 1, unit: 'kg', cost: 0 }] });
  const [saving, setSaving] = useState(false);

  const fetchData = async () => {
    try { const res = await api.get('/factory/bom'); setBoms(res.data); } catch { toast.error('Failed'); }
  };
  useEffect(() => { fetchData(); }, []);

  const addMaterial = () => setForm({...form, materials: [...form.materials, { name: '', quantity: 1, unit: 'kg', cost: 0 }]});
  const updateMaterial = (i, field, val) => { const m = [...form.materials]; m[i][field] = val; setForm({...form, materials: m}); };
  const removeMaterial = (i) => setForm({...form, materials: form.materials.filter((_, idx) => idx !== i)});

  const totalMaterialCost = form.materials.reduce((s, m) => s + (parseFloat(m.cost) || 0) * (parseFloat(m.quantity) || 0), 0);
  const totalCost = totalMaterialCost + (parseFloat(form.labor_cost) || 0) + (parseFloat(form.overhead_cost) || 0);

  const handleSave = async () => {
    if (!form.product_name) return toast.error('Product name required');
    setSaving(true);
    try {
      const payload = { ...form, labor_cost: parseFloat(form.labor_cost) || 0, overhead_cost: parseFloat(form.overhead_cost) || 0, yield_quantity: parseFloat(form.yield_quantity) || 1 };
      if (editing) { await api.put(`/factory/bom/${editing}`, payload); toast.success('Updated'); }
      else { await api.post('/factory/bom', payload); toast.success('BOM created'); }
      setShowDialog(false); setEditing(null);
      setForm({ product_name: '', product_code: '', labor_cost: 0, overhead_cost: 0, yield_quantity: 1, notes: '', materials: [{ name: '', quantity: 1, unit: 'kg', cost: 0 }] });
      fetchData();
    } catch { toast.error('Failed'); }
    finally { setSaving(false); }
  };

  const handleEdit = (bom) => {
    setEditing(bom.id);
    setForm({ product_name: bom.product_name || '', product_code: bom.product_code || '', labor_cost: bom.labor_cost || 0, overhead_cost: bom.overhead_cost || 0, yield_quantity: bom.yield_quantity || 1, notes: bom.notes || '', materials: bom.materials?.length ? bom.materials : [{ name: '', quantity: 1, unit: 'kg', cost: 0 }] });
    setShowDialog(true);
  };

  const handleDelete = async (id) => {
    if (!confirm('Delete this BOM?')) return;
    try { await api.delete(`/factory/bom/${id}`); toast.success('Deleted'); fetchData(); } catch { toast.error('Failed'); }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="bom-page">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-violet-500 to-violet-600 flex items-center justify-center shadow-lg shadow-violet-500/20">
              <Factory size={22} className="text-white" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold font-outfit tracking-tight text-foreground" data-testid="bom-title">Bill of Materials</h1>
              <p className="text-[13px] text-muted-foreground mt-0.5 font-medium">Product recipes with cost breakdown</p>
            </div>
          </div>
          <Dialog open={showDialog} onOpenChange={(o) => { setShowDialog(o); if (!o) setEditing(null); }}>
            <DialogTrigger asChild>
              <Button className="rounded-xl h-9 text-[13px]" data-testid="new-bom-btn"><Plus size={14} className="mr-1.5" />New BOM</Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[85dvh] overflow-y-auto" data-testid="bom-dialog">
              <DialogHeader><DialogTitle className="font-outfit">{editing ? 'Edit' : 'New'} Bill of Materials</DialogTitle></DialogHeader>
              <div className="space-y-4 mt-2">
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                  <div><Label className="text-[12px]">Product Name *</Label><Input value={form.product_name} onChange={e => setForm({...form, product_name: e.target.value})} data-testid="bom-product-name" /></div>
                  <div><Label className="text-[12px]">Product Code</Label><Input value={form.product_code} onChange={e => setForm({...form, product_code: e.target.value})} /></div>
                  <div><Label className="text-[12px]">Yield Qty</Label><Input type="number" value={form.yield_quantity} onChange={e => setForm({...form, yield_quantity: e.target.value})} /></div>
                </div>

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <Label className="text-[12px] font-semibold">Materials</Label>
                    <Button size="sm" variant="outline" onClick={addMaterial} className="h-7 text-[11px] rounded-lg"><Plus size={12} className="mr-1" />Add</Button>
                  </div>
                  <div className="space-y-2">
                    {form.materials.map((m, i) => (
                      <div key={i} className="flex gap-2 items-end">
                        <div className="flex-1"><Input placeholder="Material name" value={m.name} onChange={e => updateMaterial(i, 'name', e.target.value)} className="h-8 text-[13px]" /></div>
                        <div className="w-20"><Input type="number" placeholder="Qty" value={m.quantity} onChange={e => updateMaterial(i, 'quantity', e.target.value)} className="h-8 text-[13px]" /></div>
                        <div className="w-20"><Input placeholder="Unit" value={m.unit} onChange={e => updateMaterial(i, 'unit', e.target.value)} className="h-8 text-[13px]" /></div>
                        <div className="w-24"><Input type="number" placeholder="Cost/unit" value={m.cost} onChange={e => updateMaterial(i, 'cost', e.target.value)} className="h-8 text-[13px]" /></div>
                        {form.materials.length > 1 && (
                          <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-red-400" onClick={() => removeMaterial(i)}><Trash2 size={12} /></Button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div><Label className="text-[12px]">Labor Cost</Label><Input type="number" value={form.labor_cost} onChange={e => setForm({...form, labor_cost: e.target.value})} /></div>
                  <div><Label className="text-[12px]">Overhead Cost</Label><Input type="number" value={form.overhead_cost} onChange={e => setForm({...form, overhead_cost: e.target.value})} /></div>
                </div>

                <div className="bg-muted/50 rounded-xl p-3 text-[13px]">
                  <div className="flex justify-between"><span className="text-muted-foreground">Materials</span><span className="font-semibold">{cur()} {totalMaterialCost.toFixed(2)}</span></div>
                  <div className="flex justify-between mt-1"><span className="text-muted-foreground">Labor</span><span>{parseFloat(form.labor_cost || 0).toFixed(2)}</span></div>
                  <div className="flex justify-between mt-1"><span className="text-muted-foreground">Overhead</span><span>{parseFloat(form.overhead_cost || 0).toFixed(2)}</span></div>
                  <div className="flex justify-between mt-2 pt-2 border-t border-border font-bold text-primary"><span>Total Cost</span><span>{cur()} {totalCost.toFixed(2)}</span></div>
                </div>

                <div><Label className="text-[12px]">Notes</Label><Textarea value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} rows={2} /></div>
                <Button onClick={handleSave} disabled={saving} className="w-full rounded-xl">{saving ? 'Saving...' : editing ? 'Update BOM' : 'Create BOM'}</Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {/* BOM Cards */}
        {boms.length === 0 ? (
          <Card className="border-0 shadow-sm bg-white"><CardContent className="py-12 text-center text-muted-foreground">
            <Factory size={32} className="mx-auto mb-3 opacity-30" />
            <p className="text-[13px]">No BOMs created yet</p>
          </CardContent></Card>
        ) : (
          <div className="grid gap-4 md:grid-cols-2">
            {boms.map(bom => (
              <Card key={bom.id} className="border-0 shadow-sm bg-white hover:shadow-md transition-shadow" data-testid="bom-card">
                <CardContent className="pt-5 pb-4 px-5">
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <h3 className="font-semibold text-foreground text-[15px]">{bom.product_name}</h3>
                      {bom.product_code && <Badge variant="outline" className="text-[10px] mt-1">{bom.product_code}</Badge>}
                    </div>
                    <div className="flex gap-1">
                      <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => handleEdit(bom)}><Edit size={13} /></Button>
                      <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-error" onClick={() => handleDelete(bom.id)}><Trash2 size={13} /></Button>
                    </div>
                  </div>
                  <div className="space-y-1.5 text-[12px]">
                    {bom.materials?.slice(0, 4).map((m, i) => (
                      <div key={i} className="flex justify-between text-muted-foreground">
                        <span>{m.name} ({m.quantity} {m.unit})</span>
                        <span className="font-semibold">{cur()} {((m.cost || 0) * (m.quantity || 0)).toFixed(2)}</span>
                      </div>
                    ))}
                    {(bom.materials?.length || 0) > 4 && <p className="text-muted-foreground">+{bom.materials.length - 4} more materials</p>}
                  </div>
                  <div className="mt-3 pt-2 border-t border-border flex items-center justify-between">
                    <span className="text-[11px] text-muted-foreground">Yield: {bom.yield_quantity || 1} units</span>
                    <span className="font-bold text-primary text-[14px]"><DollarSign size={13} className="inline mr-0.5" />{cur()} {(bom.total_cost || 0).toFixed(2)}</span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
